import React, { useCallback, useMemo, useState } from 'react';
import { Button, ButtonStyles, MultilineInput, TextInput, Toggle } from '@silverfort/sf-components';
import { CustomExposureConfig, SaveExposuresConfigPayload } from '../types';
import ScopeWrapper, { ScopeWrapperState } from './ScopeWrapper';
import { SCOPES_OPTIONS } from '../../../../sharedComponents/scope/consts';
import { TAB_OPTIONS } from '../../../../sharedComponents/EntitiesDualSelectList';
import { AD_ENTITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';

interface CustomExposureConfigurationContentProps {
    config: CustomExposureConfig;
    onSave: (config: SaveExposuresConfigPayload) => void;
    onDelete?: (id: string) => void;
    isNew?: boolean;
    isSaving?: boolean;
}

const CustomExposureConfigurationContent: React.FC<CustomExposureConfigurationContentProps> = ({
    config: customExposureConfig,
    onSave,
    onDelete,
    isNew = false,
    isSaving = false
}) => {
    const [formData, setFormData] = useState({
        id: customExposureConfig.id,
        name: customExposureConfig.displayName,
        description: customExposureConfig.description,
        category: customExposureConfig.category,
        isActive: customExposureConfig.isActive,
        entityType: customExposureConfig.entityType,
        isStatic: customExposureConfig.isStatic
    });

    const [includedScope, setIncludedScope] = useState<ScopeWrapperState>({
        entities: customExposureConfig.usersAndGroups ?? [],
        conditions: customExposureConfig.conditions ?? []
    });
    const [excludedScope, setExcludedScope] = useState<ScopeWrapperState>({
        entities: customExposureConfig.excludedUsersAndGroups ?? [],
        conditions: customExposureConfig.excludedConditions ?? []
    });
    const [isOutOfScopeEnabled, setIsOutOfScopeEnabled] = useState(
        !!(customExposureConfig.excludedUsersAndGroups.length || customExposureConfig.excludedConditions.length)
    );

    const updateDescription = (description: string) => setFormData((prev) => ({ ...prev, description }));
    const updateName = (name: string) => setFormData((prev) => ({ ...prev, name }));

    const isValid = useMemo(() => {
        const hasInclude = includedScope.entities.length > 0 || includedScope.conditions.length > 0;
        const hasExcludeOk =
            !isOutOfScopeEnabled || excludedScope.entities.length > 0 || excludedScope.conditions.length > 0;
        return hasInclude && hasExcludeOk && formData.name.trim().length;
    }, [formData.name, includedScope, excludedScope, isOutOfScopeEnabled]);

    const addEntitiesTabsOptions = useMemo(() => {
        return customExposureConfig.entityType === AD_ENTITY_TYPES.SERVER
            ? [TAB_OPTIONS.ALL, TAB_OPTIONS.SERVERS, TAB_OPTIONS.GROUPS, TAB_OPTIONS.OUS]
            : [TAB_OPTIONS.ALL, TAB_OPTIONS.USERS, TAB_OPTIONS.GROUPS, TAB_OPTIONS.OUS];
    }, [customExposureConfig.entityType]);

    const save = useCallback(() => {
        if (!isValid) return;

        const customInsight: SaveExposuresConfigPayload = {
            ...formData,
            excludedUsersAndGroups: excludedScope.entities,
            excludedConditions: excludedScope.conditions,
            usersAndGroups: includedScope.entities,
            conditions: includedScope.conditions,
            lastModified: new Date().getTime()
        };

        onSave(customInsight);
    }, [formData, excludedScope, includedScope, isValid, onSave]);

    return (
        <div className="flex flex-col gap-4 justify-between w-full h-full overflow-hidden">
            <div className="flex flex-col gap-4 p-6 pb-0 w-full h-full overflow-y-auto">
                <div className="w-full border border-gray-200 rounded-lg">
                    <h3 className="text-13 font-medium border-b border-gray-200 p-4">Exposure info</h3>
                    <div className="space-y-4 p-6">
                        <div>
                            <label className="block text-13 font-medium  mb-2">Name *</label>
                            <TextInput
                                type="text"
                                value={formData.name}
                                onChange={updateName}
                                placeholder="Custom exposure"
                                className="w-full"
                            />
                        </div>
                        <div>
                            <label className="block text-13 font-medium  mb-2">Description</label>
                            <MultilineInput
                                value={formData.description}
                                onChange={updateDescription}
                                placeholder="Exposure details"
                                rows={3}
                                className="w-full px-3"
                            />
                        </div>
                    </div>
                </div>

                <div className="flex gap-4 h-full w-full">
                    <div className="flex flex-col justify-between h-full w-full">
                        <div className="flex flex-col gap-4 h-full">
                            <ScopeWrapper
                                scope={SCOPES_OPTIONS.IN_SCOPE}
                                state={includedScope}
                                onChange={setIncludedScope}
                                emptyStateTitle="No assets added"
                                emptyStateDescription="Add assets and select specific services"
                                tabOptions={addEntitiesTabsOptions}
                                addEntityTitle="Add Entity"
                            />

                            <div className="flex items-center gap-2">
                                <Toggle
                                    className="mt-1"
                                    checked={isOutOfScopeEnabled}
                                    onChange={(checked) => setIsOutOfScopeEnabled(checked)}
                                    disabled={excludedScope.entities.length > 0 || excludedScope.conditions.length > 0}
                                />
                                <span className="text-13">Out of scope</span>
                            </div>

                            {(isOutOfScopeEnabled ||
                                excludedScope.entities.length > 0 ||
                                excludedScope.conditions.length > 0) && (
                                <ScopeWrapper
                                    scope={SCOPES_OPTIONS.OUT_SCOPE}
                                    state={excludedScope}
                                    onChange={setExcludedScope}
                                    emptyStateTitle="None excluded"
                                    emptyStateDescription="Add naming conventions or AD objects"
                                    tabOptions={addEntitiesTabsOptions}
                                    addEntityTitle="Add Entity"
                                />
                            )}

                            {!isNew && onDelete && (
                                <div className="flex items-center">
                                    <Button
                                        buttonStyle={ButtonStyles.Danger}
                                        text="Delete Exposure"
                                        onClick={() => onDelete(customExposureConfig.id)}
                                        disabled={isSaving}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex justify-end border-t border-gray-200 bg-sf-white py-4 px-6">
                <Button
                    text={isSaving ? 'Saving...' : 'Apply'}
                    buttonStyle={ButtonStyles.PrimarySolid}
                    disabled={isSaving || !isValid}
                    onClick={save}
                />
            </div>
        </div>
    );
};

export default CustomExposureConfigurationContent;
